import { memo, useState, useCallback, useMemo } from "react";
import { PencilIcon, PlusIcon } from "@heroicons/react/outline";

import { useRolesData, useRolesColumns } from "./hooks";
import { CreateRoleDialog } from "./CreateRoleDialog";
import { UpdateRoleDialog } from "./UpdateRoleDialog";

import { useDeleteOrganizationRole } from "@/queries/organizations";
import { useDataTableDeleteConfirm } from "@/hooks/useDataTableDeleteConfirm";
import { DataTable } from "@/components/ui/ag-grid-table";
import { TableColumn } from "@/components/ui/TableV2";
import { Button } from "@/components/ui/button";
import { useIsOrgOwnerById } from "@/hooks/organizations";
import { Role } from "@/types/organization-roles";

interface RolesListProps {
  organizationId: number;
}

export const RolesList = memo(({ organizationId }: RolesListProps) => {
  const onRequestDeleteConfirm = useDataTableDeleteConfirm();
  const { filteredData, search, setSearch, isLoading } = useRolesData(organizationId);
  const { columns } = useRolesColumns();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const isOrgOwner = useIsOrgOwnerById(organizationId);
  const deleteRole = useDeleteOrganizationRole();

  const handleDelete = useCallback(
    (role?: Role) => {
      if (role) {
        deleteRole.mutate({ organizationId, roleId: role.id });
      }
    },
    [deleteRole, organizationId],
  );

  const getDeleteDisabled = useCallback((role?: Role) => {
    if (!role) {
      return true;
    }
    return role.organization_id === null;
  }, []);

  const handleOpenUpdateDialog = useCallback((role?: Role) => {
    if (!role) {
      return;
    }
    setSelectedRole(role);
    setIsUpdateDialogOpen(true);
  }, []);

  const handleUpdateOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSelectedRole(null);
    }
    setIsUpdateDialogOpen(open);
  }, []);

  const actions = useMemo(
    () =>
      isOrgOwner
        ? [
            {
              id: "update-role",
              label: "Update role",
              icon: PencilIcon,
              onClick: handleOpenUpdateDialog,
              getDisabled: (role?: Role) => role?.organization_id === null,
              tooltipContent: (role?: Role) =>
                role?.organization_id === null ? "Default roles cannot be updated" : undefined,
            },
          ]
        : undefined,
    [handleOpenUpdateDialog, isOrgOwner],
  );

  return (
    <>
      <DataTable<Role>
        data={isLoading ? [] : filteredData}
        columns={columns as TableColumn<Role>[]}
        actions={actions}
        searchPlaceholder="Search roles"
        search={search}
        setSearch={setSearch}
        hasSearch
        objectName="role"
        emptyStateMessage="No roles found"
        isLoading={isLoading}
        rowsDeletable={isOrgOwner}
        onRequestDeleteConfirm={onRequestDeleteConfirm}
        onDelete={handleDelete}
        getDeleteDisabled={getDeleteDisabled}
        getDeleteTooltip={(role?: Role) =>
          role?.organization_id === null ? "Default roles cannot be deleted" : undefined
        }
        deleteConfirmSaveVariant="destructive"
        deleteConfirmText="Delete Role"
        gridOptions={{
          suppressHorizontalScroll: false,
          alwaysShowHorizontalScroll: false,
          suppressScrollOnNewData: true,
        }}
        className="[&_.ag-cell-value]:items-center"
        action={
          isOrgOwner ? (
            <Button onClick={() => setIsCreateDialogOpen(true)} variant="outline">
              <PlusIcon className="mr-2 h-4 w-4" />
              Create Role
            </Button>
          ) : undefined
        }
      />
      <CreateRoleDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        organizationId={organizationId}
      />
      <UpdateRoleDialog
        open={isUpdateDialogOpen}
        onOpenChange={handleUpdateOpenChange}
        organizationId={organizationId}
        role={selectedRole}
      />
    </>
  );
});

RolesList.displayName = "RolesList";
